import { EmptyState, FlexColumn, Loading } from '@gorila-shared-ui/components';
import { Accordion, Panel } from 'baseui/accordion';
import { useEffect } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { useStyles } from '../../../hooks/useStyles';
import { getHistoricTelemetry } from '../../../services/reportsService';
import { apiParamsState, isAllWorkspacesSelect } from '../../../storage/app';
import {
  activeTelemetryState,
  loadingTelemetryState,
  reportEndDateState,
  reportSelectedAssetIdsState,
  reportStartDateState,
  reportTelemetryStates,
} from '../../../storage/reports';
import { HistoricTelemetry } from '../../../types/currentState';
import { TelemetryReportTableFromAsset } from './TelemetryReportTableFromAsset';
import TelemetryReportTitle from './TelemetryReportTitle';

export function TelemetryReports() {
  const { css } = useStyles();
  const selectedAssetIds = useRecoilValue(reportSelectedAssetIdsState);
  const endDate = useRecoilValue(reportEndDateState);
  const startDate = useRecoilValue(reportStartDateState);
  const apiParams = useRecoilValue(apiParamsState);
  const isAllWorkspacesSelected = useRecoilValue(isAllWorkspacesSelect);
  const setActiveReport = useSetRecoilState(activeTelemetryState);
  const [loadingTelemetry, setLoadingTelemetry] = useRecoilState(loadingTelemetryState);
  const setReportHistorics = useSetRecoilState(reportTelemetryStates);
  const resetTelemetryHistorics = useResetRecoilState(reportTelemetryStates);

  useEffect(() => {
    return () => {
      resetTelemetryHistorics();
    };
  }, []);

  useEffect(() => {
    if (!selectedAssetIds?.length || !startDate || !endDate || !apiParams) return;

    const promises = selectedAssetIds.map((assetId) =>
      getHistoricTelemetry(
        apiParams.clientId,
        assetId,
        startDate,
        endDate,
        isAllWorkspacesSelected ? undefined : apiParams.subClientId,
      ),
    );
    const loadHistoricTelemetry = async () => {
      setLoadingTelemetry(true);
      const responses = await Promise.allSettled(promises);

      const newHistorics: { [key: string]: HistoricTelemetry[] } = {};
      responses.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          const {
            value: { historicTelemetry, error },
          } = result;
          if (!error) {
            newHistorics[selectedAssetIds[index]] = historicTelemetry ?? [];
          }
        }
      });
      setReportHistorics(newHistorics);

      if (newHistorics[selectedAssetIds[0]]) {
        setActiveReport(selectedAssetIds[0]);
      }
      setLoadingTelemetry(false);
    };

    loadHistoricTelemetry();
  }, [selectedAssetIds, startDate, endDate]);

  if (loadingTelemetry) {
    return <Loading />;
  }

  if (!selectedAssetIds?.length) {
    return <EmptyState title="Selecciona un activo para visualizar el reporte." />;
  }

  return (
    <FlexColumn
      gap={0}
      classNames={css({ flex: 1, overflow: 'hidden' })}
    >
      <AutoSizer>
        {({ width, height }) => (
          <div style={{ width, height, overflow: 'auto' }}>
            <Accordion
              key={selectedAssetIds[0]}
              initialState={{ expanded: [selectedAssetIds[0]] }}
              onChange={({ expanded }) => setActiveReport(expanded.length ? (expanded[0] as string) : undefined)}
            >
              {selectedAssetIds.map((assetId) => (
                <Panel
                  key={assetId}
                  title={<TelemetryReportTitle assetId={assetId} />}
                  overrides={{
                    Content: {
                      style: {
                        paddingTop: 0,
                      },
                    },
                  }}
                >
                  <TelemetryReportTableFromAsset assetId={assetId} />
                </Panel>
              ))}
            </Accordion>
          </div>
        )}
      </AutoSizer>
    </FlexColumn>
  );
}
